/* QR decode policy — one rule for both scan surfaces (CD docket 2026-09-22).
 *
 * `BarcodeDetector` is not a baseline web API: Safari (macOS + iOS) and Firefox
 * do not ship it at all, and Chrome/Edge on Windows and Linux expose the class
 * while `getSupportedFormats()` resolves to `[]` and `detect()` rejects — the
 * trap that made the camera go live and silently never decode. So the policy is
 * native-if-it-really-supports-qr_code, jsQR otherwise, nothing only when both
 * are missing.
 *
 * Data: n/a — pure browser capability resolution, no ops, no network.
 * Consumers: screens/eventday/EventDay.tsx (CheckinOverlay) and, in its own
 * hand-written copy, app/public/calendar-control/control.dc.html (`scanAttach`).
 * Keep the two in step: the board is a static page with no bundler, so it loads
 * `vendor/jsqr-1.4.0.js` with a <script> tag instead of importing this module.
 */

export type QrDetect = (video: HTMLVideoElement) => Promise<string | null>;
export type QrDecoder = { kind: "native" | "jsqr"; detect: QrDetect } | null;

export type BarcodeDetectorLike = { detect: (src: CanvasImageSource) => Promise<{ rawValue?: string }[]> };
export type BarcodeDetectorCtor = (new (opts?: { formats?: string[] }) => BarcodeDetectorLike) & {
  getSupportedFormats?: () => Promise<string[]>;
};
export type JsQrFn = (
  data: Uint8ClampedArray,
  width: number,
  height: number,
  opts?: { inversionAttempts?: string },
) => { data?: string } | null;

/* jsQR is CPU-bound: a full 1080p frame every 400 ms janks a phone. The frame
 * is downscaled so its longer side is at most this before the read. */
export const MAX_DECODE_EDGE = 640;

/* Draws the RAW video frame — never the CSS-mirrored preview. The front-camera
 * `scaleX(-1)` is a style transform and does not reach the canvas, so a
 * mirrored code decodes exactly as it does on the native path. */
export function jsqrDetect(jsQR: JsQrFn, makeCanvas: () => HTMLCanvasElement | null): QrDetect {
  let canvas: HTMLCanvasElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;
  return async (video) => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;
    const k = Math.min(1, MAX_DECODE_EDGE / Math.max(vw, vh));
    const w = Math.max(1, Math.round(vw * k));
    const h = Math.max(1, Math.round(vh * k));
    if (!canvas) canvas = makeCanvas();
    if (!canvas) return null;
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; ctx = null; }
    if (!ctx) ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const hit = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" });
    return (hit && hit.data) || null;
  };
}

export async function pickQrDecoder(env: {
  detector?: BarcodeDetectorCtor | null;
  jsQR?: JsQrFn | null;
  makeCanvas?: () => HTMLCanvasElement | null;
}): Promise<QrDecoder> {
  const makeCanvas = env.makeCanvas || (() => (typeof document === "undefined" ? null : document.createElement("canvas")));
  const fallback = (): QrDecoder => (env.jsQR ? { kind: "jsqr", detect: jsqrDetect(env.jsQR, makeCanvas) } : null);
  const Det = env.detector;
  if (!Det) return fallback();
  try {
    const formats = Det.getSupportedFormats ? await Det.getSupportedFormats() : ["qr_code"];
    if (!formats || formats.indexOf("qr_code") < 0) return fallback();
    const det = new Det({ formats: ["qr_code"] });
    return {
      kind: "native",
      detect: async (video) => {
        const codes = await det.detect(video);
        const raw = (codes || []).map((c) => c.rawValue).filter(Boolean)[0];
        return raw ? String(raw) : null;
      },
    };
  } catch {
    return fallback();
  }
}
